import type { ChallengeWithCountsDto } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import type { FC } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

type TechChallengeBarChartProps = {
  challenge: ChallengeWithCountsDto;
};

type ChartDataPoint = {
  color: string;
  count: number;
  name: string;
  percentage: number;
};

type CustomTooltipProps = {
  active?: boolean;
  payload?: {
    color: string;
    dataKey: string;
    name: string;
    payload: ChartDataPoint;
    value: number;
  }[];
};

const ALL_TECHNOLOGIES_COLOR = '#e5e7eb';
const PHASE_COLOR = '#3D2EFF1A';

export const TechChallengeBarChart: FC<TechChallengeBarChartProps> = (props) => {
  const { challenge } = props;

  const totalTechnologyCount = challenge.totalTechnologyCount;

  const chartData: ChartDataPoint[] = [
    {
      color: ALL_TECHNOLOGIES_COLOR,
      count: challenge.totalTechnologyCount,
      name: 'All Technologies',
      percentage: 100,
    },
    ...challenge.phases
      .sort((a, b) => a.position - b.position)
      .map((phase, index) => ({
        color: PHASE_COLOR,
        count: phase.matchCount,
        name: `Phase ${index + 1}: ${phase.name}`,
        percentage: totalTechnologyCount > 0 ? (phase.matchCount / totalTechnologyCount) * 100 : 0,
      })),
  ];

  const CustomTooltip: FC<CustomTooltipProps> = (tooltipProps) => {
    const { active, payload } = tooltipProps;

    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;

      return (
        <div className="flex flex-col gap-y-1 bg-white p-2 border border-gray-200 rounded shadow-sm">
          <div className="font-medium text-gray-700">{data.name}</div>
          <div className="text-sm text-gray-600">
            {data.count} {data.count === 1 ? 'match' : 'matches'}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Tile header={{ title: 'Challenge Funnel' }}>
      <div className="w-full h-[400px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ bottom: 5, left: 20, right: 50, top: 5 }}
          >
            <CartesianGrid
              horizontal={false}
              stroke="#eee"
            />
            <YAxis
              axisLine={false}
              dataKey="name"
              hide
              tickLine={false}
              type="category"
            />
            <XAxis
              domain={[0, totalTechnologyCount]}
              hide
              type="number"
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: '#f9fafb' }}
            />
            <Bar
              barSize={60}
              dataKey="count"
              radius={4}
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                />
              ))}
              <LabelList
                className="fill-gray-700 font-medium"
                content={(props) => {
                  const { x, y, width, height, index } = props;
                  const data = chartData[index ?? 0];
                  if (!data) return null;

                  return (
                    <text
                      x={Number(x) + 8}
                      y={Number(y) + Number(height) / 2}
                      fill="#374151"
                      fontSize={12}
                      fontWeight={500}
                      textAnchor="start"
                      dominantBaseline="middle"
                    >
                      {data.name} – {data.count}
                    </text>
                  );
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Tile>
  );
};
